/** Known text/code extensions — fast-path: bypass content sniffing. */
const TEXT_EXTS = new Set([
  // code
  "js", "jsx", "ts", "tsx", "mjs", "cjs",
  "py", "pyi", "pyw", "rb", "rs", "go", "java", "kt", "kts", "scala", "swift",
  "c", "cpp", "cc", "cxx", "h", "hpp", "hh",
  "cs", "vb", "fs", "ml", "mli", "hs",
  "php", "phtml", "lua", "pl", "pm", "r", "dart", "ex", "exs", "erl",
  "sh", "bash", "zsh", "fish", "ksh", "ps1", "psm1", "bat", "cmd",
  // markup / data / config
  "html", "htm", "xhtml", "xml", "svg",
  "css", "scss", "sass", "less", "styl",
  "json", "json5", "jsonc", "ndjson", "geojson",
  "yaml", "yml",
  "toml", "ini", "cfg", "conf", "config", "properties", "env",
  "tsv", "csv", "psv",
  "md", "mdx", "markdown", "rst", "adoc", "asciidoc", "org", "tex",
  "log", "out", "err",
  "sql", "graphql", "gql", "proto", "thrift", "avsc",
  "dockerfile", "containerfile",
  "makefile", "mk", "gradle",
  "patch", "diff",
  "gitignore", "gitattributes", "gitmodules", "gitconfig", "dockerignore", "npmignore",
  "editorconfig", "prettierrc", "eslintrc", "babelrc", "browserslistrc",
  "vue", "svelte", "astro", "njk", "ejs", "pug", "hbs", "mustache", "twig",
  "asm", "s",
  "txt", "text", "nfo", "readme", "license", "authors", "changelog", "todo",
  "dat",
]);

/** Files commonly stored without extension that are still plain text. */
const TEXT_BASENAMES = new Set([
  "dockerfile", "containerfile", "makefile", "rakefile", "gemfile", "procfile",
  "readme", "license", "licence", "authors", "changelog", "contributing", "notice",
  "todo", "copying", "install", "news",
]);

function fileBasename(file) {
  return (file.name || "").toLowerCase();
}

function fileExt(file) {
  const name = fileBasename(file);
  if (!name.includes(".")) return "";
  return name.split(".").pop();
}

/** Fast classification by extension or known basename. */
export function isTextLikeFile(file) {
  const mime = (file.type || file.mime || "").toLowerCase();
  if (mime.startsWith("text/")) return true;
  if (mime === "application/json" || mime === "application/xml") return true;
  if (mime === "application/x-sh" || mime === "application/javascript") return true;

  const name = fileBasename(file);
  if (TEXT_BASENAMES.has(name)) return true;

  const ext = fileExt(file);
  return ext ? TEXT_EXTS.has(ext) : false;
}

/**
 * Content-based detection — used when the extension is unknown.
 * Mirrors `git`'s heuristic: reject on NUL bytes; require ≥85% printable.
 */
const SNIFF_BYTES = 4096;
const PRINTABLE_THRESHOLD = 0.85;

export async function looksLikeText(blob) {
  if (!blob || blob.size === 0) return false;
  const slice = blob.slice(0, Math.min(SNIFF_BYTES, blob.size));
  const buf = await slice.arrayBuffer();
  const bytes = new Uint8Array(buf);

  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] === 0) return false;
  }

  try {
    new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return false;
  }

  let printable = 0;
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b === 9 || b === 10 || b === 13) { printable++; continue; }
    if (b >= 32 && b < 127) { printable++; continue; }
    if (b >= 0x80) { printable++; continue; }
  }
  return printable / bytes.length >= PRINTABLE_THRESHOLD;
}

const MAX_TEXT_PREVIEW_BYTES = 4 * 1024 * 1024;

/** Decide whether to render a file as text, using extension first and content as fallback. */
export async function shouldPreviewAsText(file, blob) {
  if (isTextLikeFile(file)) return true;
  if (!blob || blob.size > MAX_TEXT_PREVIEW_BYTES) return false;
  return looksLikeText(blob);
}

export async function readTextFromUpload(file, maxBytes = 32 * 1024) {
  if (!isTextLikeFile(file) || file.size > maxBytes) return undefined;
  try {
    return await file.text();
  } catch {
    return undefined;
  }
}
